<template>
  <div
    class="pay-buttons"
    v-show="selectedSkuList.length > 0 && !hasProcessingSelected && !hasUndeliverableSelected"
  >
    <div
      v-show="paymentType === 0"
      class="pay-buttons__paypal"
      ref="paypalButtonRef"
    />
    <Button
      v-show="paymentType === 1"
      @click="handleBankPayment"
      class="pay-buttons__button"
      type="primary"
      :loading="loading"
    >
      Place Order
    </Button>
  </div>
</template>

<script setup>
import storage from '@/utils/storage'
import { loadScript } from '@paypal/paypal-js'
import { fetchData } from '@/utils/fetch'
import { useCartStore } from '@/pinia/cart'
import { gaAnalytics, buriedPointUtil, metaPixelTrack } from '@/utils/utils'
import { useRouter } from 'vue-router'
import { usePlugin } from '@/hooks/usePlugin'
import { Button } from '@/components/Button'
import { useCart } from '../hooks/useCart'
import { useOrder } from '../hooks/useOrder'
import { useCurrencyStore } from '@/pinia/currency'
import { deviceType } from '@/utils/constants'
import { createCartOrder } from '@/api/order'

const loading = ref(false)

const currency = useCurrencyStore()

const { hasProcessingSelected, hasUndeliverableSelected, paymentType, selectedSkuList, selectedSkuCount, totalAmount, discountAmountUSD } = useOrder()

const { $toast } = usePlugin()

const router = useRouter()

const cartStore = useCartStore()

const { channelCode } = useCart()

const paypalButtonRef = useTemplateRef('paypalButtonRef')

const clientId = ref(0)

const getClientId = async () => {
  const { code, data } = await fetchData('/appb/b2b/pay/getPaypalClientId', {
    method: 'get'
  })
  if (code === 200) {
    clientId.value = data
  }
  return 'Ad-U4AlBoG22sqpaaAcLGpCdrJqSK7jZXHebOxhfyrr-ngI9JCvZ6GmIuUKs1ZWR99t1xmx6d2y6S-RP'
}

const initPaypal = async () => {
  try {
    const paypal = await loadScript({
      clientId: clientId.value,
      components: 'buttons',
      disableFunding: 'paylater'
    })
    const config = {
      style: {
        shape: 'pill'
      },
      createOrder: async () => {
        if (deviceType !== 'MOBILE') {
          gaAnalytics('p_place_order_click', 'p_place_order_click')
          gaAnalytics('p_paypal_click', 'p_paypal_click')
        }

        const data = await createOrder('PAYPAL_STANDARD_B2B')
        if (deviceType === 'MOBILE') {
          buriedPointUtil({ eventId: 'MPPP601' })
        } else {
          buriedPointUtil({ eventId: 'PPP601' })
        }
        storage.set('tradeCode', data.tradeCode)
        return data.thirdTradeCode
      },
      onApprove: async () => {
        try {
          const tradeCode = storage.get('tradeCode')
          const orderNo = storage.get('orderNo')

          await fetchData('/appb/b2b/order/completePayAction', {
            method: 'POST',
            body: JSON.stringify({
              orderNo,
              tradeCode
            })
          })

          if (deviceType === 'MOBILE') {
            gaAnalytics('mp_pay_success', 'mp_pay_success')
            gaAnalytics('PM_Pay_Success', 'PM_Pay_Success')
          } else {
            gaAnalytics('P_Pay_Success', 'P_Pay_Success')
            gaAnalytics('p_pay_success', 'p_pay_success')
          }

          /**
           * Meta Pixel 埋点：银行转账提交支付审核
           */
          metaPixelTrack('POP401', 'Purchase', {
            content_ids: [...new Set(selectedSkuList.value.map((item) => item.spuId))],
            content_type: 'product_group',
            currency: '$',
            value: 9,
            num_items: selectedSkuCount
          })

          if (storage.get('buy-again')) {
            if (deviceType === 'MOBILE') {
              gaAnalytics('mp_buy_again_success', 'mp_buy_again_success')
            } else {
              gaAnalytics('p_buy_again_success', 'p_buy_again_success')
            }
            storage.remove('buy-again')
          }
          storage.remove('tradeCode')
          storage.remove('orderNo')

          if (deviceType === 'MOBILE') {
            router.push({ name: 'orders' })
          } else {
            router.push(`/bankPayment/${orderNo}?type=paidSuccess`)
          }
        } catch (err) {
          console.error('支付完成失败:', err)
          throw err
        }
      },
      onCancel: () => {
        storage.remove('tradeCode')
        storage.remove('orderNo')
        router.push('/orders?status=1')
      },
      onError: (error) => {
        storage.remove('tradeCode')
        storage.remove('orderNo')

        /**
         * 非价格相关变动导致的下单失败都跳转到 订单列表 页
         */
        if (![300001, 300002, 400001].includes(error.code)) {
          router.push('/orders?status=1')
        }
      }
    }
    paypal.Buttons(config).render(paypalButtonRef.value)
  } catch (error) {
    console.log('init paypal...')
    setTimeout(() => {
      initPaypal()
    }, 300)
  }
}

const createOrder = async (paymentMethodCode) => {
  try {
    loading.value = true
    const data = await createCartOrder({
      fulfillmentAmount: totalAmount.value,
      discountAmountUsd: discountAmountUSD.value,
      shipChannel: channelCode.value,
      currency: currency.current.targetCurrency,
      details: selectedSkuList.value.map((item) => ({
        cartId: item.cartId,
        quantity: item.quantity,
        skuId: item.skuId
      })),
      address: storage.get('addressForm')
    })
    storage.set('orderNo', data)
    if (paymentMethodCode === 'BANK_TRANSFER_B2B') {
      return data
    }
    const { code: payCode, data: payData, msg: payMsg } = await fetchData(
      '/appb/b2b/order/createOrderPay',
      {
        method: 'POST',
        body: JSON.stringify({
          orderNo: data,
          paymentMethodCode,
          receiptInfos: selectedSkuList.value.map((item) => item.skuImageUrl)
        })
      }
    )
    if (payCode === 200) {
      if (deviceType === 'MOBILE') {
        buriedPointUtil({ eventId: 'MPPP201', others: { type: 'PayPal' } })
        gaAnalytics('mp_place_order_success', 'mp_place_order_success')
      } else {
        buriedPointUtil({ eventId: 'PPP201', others: { type: 'PayPal' } })
        gaAnalytics('p_place_order_success', 'p_place_order_success')
      }
      cartStore.updateCartCount()
      return payData
    } else {
      throw new Error(payMsg)
    }
  } catch (error) {
    $toast(error.msg || error.message, { type: 'error' })

    throw error
  } finally {
    loading.value = false
  }
}

const handleBankPayment = async () => {
  if (!selectedSkuList.value.length) {
    return
  }
  if (deviceType === 'PC') {
    gaAnalytics('p_place_order_click', 'p_place_order_click')
    gaAnalytics('p_pay_with_bank_click', 'p_pay_with_bank_click')
  }
  const data = await createOrder('BANK_TRANSFER_B2B')

  cartStore.updateCartCount()
  if (data) {
    if (deviceType === 'MOBILE') {
      buriedPointUtil({ eventId: 'MPPP601' })
      buriedPointUtil({ eventId: 'MPPP201', others: { type: 'Bank' } })
      gaAnalytics('mp_place_order_success', 'mp_place_order_success')
    } else {
      buriedPointUtil({ eventId: 'PPP601' })
      buriedPointUtil({ eventId: 'PPP201', others: { type: 'Bank' } })
      gaAnalytics('p_place_order_success', 'p_place_order_success')
    }

    router.push(`/bankPayment/${data}`)
    $toast('Order created successfully', { type: 'success' })
  }
}

onMounted(async () => {
  await getClientId()
  initPaypal()
})
</script>

<style lang='less' scoped>
.pay-buttons {
  overflow: hidden;

  &__paypal {
    margin-bottom: -32px;
  }

  &__button {
    background-color: #4681F9;
  }
}
</style>
